import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Factory, Package, Sparkles, PackageCheck, FileText, Wallet, Truck, Quote, Star, FileUp, ShieldCheck, Ruler, IndianRupee } from 'lucide-react';
import { api, inr, imgUrl } from '@/lib/api';
import ProductCard from '@/components/product/ProductCard';
import ProductDetailModal from '@/components/product/ProductDetailModal';
import LeadCaptureModal from '@/components/LeadCaptureModal';
import HeroCarousel, { CAROUSEL_IMAGES } from '@/components/HeroCarousel';
import { useSiteSettings, buildWhatsAppUrl } from '@/hooks/useSiteSettings';
import { openWhatsAppWithTracking } from '@/lib/gtag';

const HOW_JOB = [
    { icon: <FileText />, title: 'Share drawing / cutlist' },
    { icon: <PackageCheck />, title: 'We share board count' },
    { icon: <Truck />, title: 'You ship the material' },
    { icon: <Factory />, title: 'We manufacture & dispatch' },
];
const HOW_STANDARD = [
    { icon: <FileText />, title: 'Share drawing / cutlist' },
    { icon: <PackageCheck />, title: 'We share board count' },
    { icon: <Wallet />, title: 'Approve and pay advance' },
    { icon: <Factory />, title: 'We source, manufacture & dispatch' },
];

const STANDARD_TIERS = [
    { key: 'eco', label: 'Eco', material: 'Action Tesa HDHMR Prelam Board', price: 5499, blurb: 'Pre-pasted HDHMR — quick and cost-efficient.' },
    { key: 'prime', label: 'Prime', material: 'HDHMR + Liner Laminate (Postlam)', price: 7499, blurb: 'Choose your liner for a customised interior.', highlight: true },
    { key: 'gold', label: 'Gold', material: 'Commercial Ply + Liner Laminate', price: 8499, blurb: 'Highest strength & moisture resistance.' },
];

const JOB_TIERS = [
    { key: 'prelam', label: 'Prelam Boards', price: 1200, blurb: 'Made from pre-pasted (prelam) boards. Fastest to manufacture.' },
    { key: 'postlam', label: 'Postlam Boards', price: 1500, blurb: 'Plain boards + your chosen liner laminate, pasted in-house.' },
];

const OFFERING_CARDS = [
    {
        num: 'Offering 1',
        title: 'Job Work',
        target: 'job-work',
        color: 'bg-blue-900',
        icon: <Factory className="w-6 h-6" />,
        headline: 'Have your own design & material?',
        pitch: 'Pay flat factory labour rates. We cut, paste, drill and dispatch — you save every rupee dealers usually add.',
        cta: 'See Job Work',
    },
    {
        num: 'Offering 2',
        title: 'Standard Package',
        target: 'standard-package',
        color: 'bg-amber-600',
        icon: <Package className="w-6 h-6" />,
        headline: 'Have the design — want us to source & make it?',
        pitch: 'Kanbr procures brand-name boards, laminates and hardware, then manufactures your kitchen / wardrobe end-to-end.',
        cta: 'See Packages',
    },
    {
        num: 'Offering 3',
        title: 'Pre-Crafted Modules (DIY)',
        target: 'pre-crafted',
        color: 'bg-slate-900',
        icon: <PackageCheck className="w-6 h-6" />,
        headline: 'Want to build it yourself?',
        pitch: 'Pick from 180+ factory-made modules — base units, wardrobes, dressers — and we ship them to your doorstep.',
        cta: 'Browse Modules',
    },
    {
        num: 'Offering 4',
        title: 'Ideation & Designing',
        target: 'ideation',
        color: 'bg-blue-950',
        icon: <Sparkles className="w-6 h-6" />,
        headline: 'Want a full turnkey solution?',
        pitch: 'From ideation and 3D design to material sourcing and installation — sit back, we own every step.',
        cta: 'Start Ideation',
    },
];

const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

export default function Home() {
    const [products, setProducts] = useState([]);
    const [modalProduct, setModalProduct] = useState(null);
    const [leadOpen, setLeadOpen] = useState(false);
    const s = useSiteSettings();

    useEffect(() => {
        api.get('/products', { params: { limit: 500 } }).then((r) => setProducts(r.data)).catch(() => {});
    }, []);

    const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const showcaseGroups = useMemo(() => {
        // Group Kitchen: products whose category starts with 'Kitchen -'
        const kitchen = products.filter((p) => (p.category || '').startsWith('Kitchen -'));
        const wardrobe = products.filter((p) => p.category === 'Wardrobe');
        // Pick 4 random SKUs, one from each subcategory when possible to spread across sub-cats
        const pickFromSubs = (list, n) => {
            const bySub = {};
            for (const p of list) {
                const s = p.subcategory || '_';
                if (!bySub[s]) bySub[s] = [];
                bySub[s].push(p);
            }
            const subKeys = shuffle(Object.keys(bySub));
            const chosen = [];
            for (const k of subKeys) {
                if (chosen.length >= n) break;
                const pool = bySub[k];
                chosen.push(pool[Math.floor(Math.random() * pool.length)]);
            }
            // if still short, backfill from remaining
            if (chosen.length < n) {
                const chosenIds = new Set(chosen.map((c) => c.id));
                const rest = shuffle(list.filter((p) => !chosenIds.has(p.id)));
                chosen.push(...rest.slice(0, n - chosen.length));
            }
            return chosen.slice(0, n);
        };
        return [
            { title: 'Kitchen', filter: () => true, link: 'category=Kitchen%20-%20Base%20Units', items: pickFromSubs(kitchen, 4) },
            { title: 'Wardrobe', filter: () => true, link: 'category=Wardrobe', items: pickFromSubs(wardrobe, 4) },
        ];
    }, [products]);

    return (
        <div>
            {/* Hero */}
            <section className="relative bg-white border-b border-slate-200">
                <div className="kanbr-container grid grid-cols-1 lg:grid-cols-12 gap-8 py-10 md:py-14 items-center">
                    <div className="lg:col-span-6 rise">
                        <h1 className="font-display font-black text-slate-900 text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
                            Custom Modular<br />Wardrobes &amp; Kitchens.<br />
                            <span className="text-blue-900">Factory Finish.</span>
                        </h1>
                        <p className="text-slate-600 text-base md:text-lg mt-5 max-w-xl leading-relaxed">
                            Modern modular kitchen price without dealer markups. From Job Work manufacturing to end-to-end interior designer service — all in-house at our NCR facility.
                        </p>
                        <div className="flex flex-wrap gap-3 mt-6">
                            <button onClick={() => scrollTo('our-offerings')} className="btn-primary rounded-none" data-testid="hero-cta-catalog">
                                Explore Modules <ArrowRight className="w-4 h-4" />
                            </button>
                            <a
                                href={buildWhatsAppUrl(s.whatsapp_number, 'Hi Kanbr, I want a project quote.')}
                                target="_blank"
                                rel="noreferrer"
                                onClick={() => openWhatsAppWithTracking(null, { source: 'home-hero', message: 'Hi Kanbr, I want a project quote.' })}
                                className="btn-outline rounded-none"
                                data-testid="hero-cta-whatsapp"
                            >
                                Talk on WhatsApp
                            </a>
                        </div>
                    </div>
                    <div className="lg:col-span-6 rise rise-2 relative">
                        <HeroCarousel images={CAROUSEL_IMAGES} interval={3000} alt="Kanbr featured project" />
                        <Link to="/estimate" data-testid="hero-get-estimate" className="absolute bottom-4 right-4 md:bottom-6 md:right-6 z-20 bg-amber-600 hover:bg-amber-700 text-white px-5 py-3 md:px-6 md:py-3.5 font-display font-semibold shadow-lg flex items-center gap-2 transition-colors">
                            <FileUp className="w-4 h-4" /> Get An Estimate
                        </Link>
                    </div>
                </div>
            </section>

            {/* USP band - between hero and Our Offerings */}
            <section style={{ backgroundColor: '#1E3A8A' }} className="text-white" data-testid="usp-band">
                <div className="kanbr-container py-3 md:py-5">
                    <div className="grid grid-cols-3 gap-2 md:gap-6 text-center">
                        <div className="flex flex-col md:flex-row items-center justify-center gap-1 md:gap-3">
                            <ShieldCheck className="w-5 h-5 md:w-6 md:h-6 text-amber-400 flex-shrink-0" />
                            <div className="font-display text-xs sm:text-sm md:text-lg font-bold leading-tight md:whitespace-nowrap">
                                <span className="block md:inline">Premium</span>{' '}
                                <span className="block md:inline">Materials</span>
                            </div>
                        </div>
                        <div className="flex flex-col md:flex-row items-center justify-center gap-1 md:gap-3">
                            <Ruler className="w-5 h-5 md:w-6 md:h-6 text-amber-400 flex-shrink-0" />
                            <div className="font-display text-xs sm:text-sm md:text-lg font-bold leading-tight md:whitespace-nowrap">
                                <span className="block md:inline">Precision</span>{' '}
                                <span className="block md:inline">Manufacturing</span>
                            </div>
                        </div>
                        <div className="flex flex-col md:flex-row items-center justify-center gap-1 md:gap-3">
                            <IndianRupee className="w-5 h-5 md:w-6 md:h-6 text-amber-400 flex-shrink-0" />
                            <div className="font-display text-xs sm:text-sm md:text-lg font-bold leading-tight md:whitespace-nowrap">
                                <span className="block md:inline">Factory</span>{' '}
                                <span className="block md:inline">Pricing</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Our Offerings — 4 cards */}
            <section id="our-offerings" className="bg-slate-50 border-b border-slate-200">
                <div className="kanbr-container py-12">
                    <div className="text-center mb-8">
                        <div className="eyebrow text-amber-700 mb-2">Choose your path</div>
                        <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 leading-tight">Our Offerings</h2>
                        <p className="text-slate-600 mt-3 text-sm md:text-base max-w-2xl mx-auto">Four ways to work with us — from a simple Job Work manufacture to a full turnkey design solution.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {OFFERING_CARDS.map((o) => (
                            <button
                                key={o.target}
                                onClick={() => scrollTo(o.target)}
                                data-testid={`action-card-${o.target}`}
                                className="text-left card-flat p-5 hover-lift group flex flex-col"
                            >
                                <div className="flex items-center gap-3 mb-3">
                                    <div className={`w-11 h-11 ${o.color} text-white flex items-center justify-center flex-shrink-0`}>{o.icon}</div>
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.24em] text-amber-700 font-semibold">{o.num}</div>
                                        <div className="font-display text-base font-bold text-slate-900 leading-tight">{o.title}</div>
                                    </div>
                                </div>
                                <div className="font-display text-sm font-semibold text-slate-900 leading-tight">{o.headline}</div>
                                <p className="text-xs text-slate-600 mt-2 leading-relaxed flex-1">{o.pitch}</p>
                                <div className="mt-3 text-xs font-medium text-blue-900 inline-flex items-center gap-1 group-hover:gap-2 transition-all">{o.cta} <ArrowRight className="w-3.5 h-3.5" /></div>
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Stats band - between Our Offerings and Job Work */}
            <section style={{ backgroundColor: '#1E3A8A' }} className="text-white" data-testid="stats-band">
                <div className="kanbr-container py-5">
                    <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                            <div className="font-display text-2xl md:text-3xl font-bold leading-none">2,400+</div>
                            <div className="text-[10px] md:text-xs text-blue-100 mt-1 uppercase tracking-wider">Projects delivered</div>
                        </div>
                        <div>
                            <div className="font-display text-2xl md:text-3xl font-bold leading-none">10 yrs</div>
                            <div className="text-[10px] md:text-xs text-blue-100 mt-1 uppercase tracking-wider">Warranty</div>
                        </div>
                        <div>
                            <div className="font-display text-2xl md:text-3xl font-bold leading-none">7 days</div>
                            <div className="text-[10px] md:text-xs text-blue-100 mt-1 uppercase tracking-wider">Lead time</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Section 1: Job Work */}
            <section id="job-work" className="bg-slate-50 border-t-4 border-t-amber-600 border-b border-b-slate-200">
                <div className="kanbr-container py-14">
                    <div className="text-center mb-8">
                        <div className="eyebrow text-slate-500 mb-2">Offering 01</div>
                        <h2 className="font-display text-2xl md:text-3xl font-bold text-amber-600 leading-tight tracking-tight">Job Work</h2>
                        <p className="text-slate-700 mt-3 text-sm md:text-base max-w-2xl mx-auto leading-relaxed"><strong className="text-slate-900">You Source. We Manufacture.</strong> Modular kitchen Job Work and wardrobe making labor charges per sq ft — transparent, factory pricing.</p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                        {HOW_JOB.map((st, i) => (
                            <div key={i} className="border border-slate-200 bg-white p-4 relative">
                                <div className="text-[10px] font-display font-bold text-amber-600 absolute top-3 right-4 text-lg">0{i + 1}</div>
                                <div className="w-9 h-9 bg-blue-900 text-white flex items-center justify-center mb-2">{st.icon}</div>
                                <h3 className="font-display font-semibold text-slate-900 text-sm leading-snug">{st.title}</h3>
                            </div>
                        ))}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {JOB_TIERS.map((t) => (
                            <div key={t.key} className="border border-slate-200 bg-white p-6">
                                <div className="eyebrow mb-1">{t.label}</div>
                                <div className="font-display text-3xl font-bold text-slate-900">₹{t.price}<span className="text-sm font-medium text-slate-500"> /board</span></div>
                                <div className="text-[11px] text-slate-500 mt-1">+ GST + Transport</div>
                                <p className="text-xs text-slate-600 mt-2 leading-relaxed">{t.blurb}</p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-8 text-center">
                        <Link to="/job-work" className="btn-primary rounded-none inline-flex" data-testid="explore-job-work">
                            Explore Job Work <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Section 2: Standard Package - Dark Blue */}
            <section id="standard-package" className="bg-slate-900 text-white">
                <div className="kanbr-container py-14">
                    <div className="text-center mb-8">
                        <div className="eyebrow text-slate-400 mb-2">Offering 02</div>
                        <h2 className="font-display text-2xl md:text-3xl font-bold text-amber-500 leading-tight tracking-tight">Standard Package</h2>
                        <p className="text-slate-300 mt-3 text-sm md:text-base max-w-2xl mx-auto leading-relaxed"><strong className="text-white">We Source. We Manufacture.</strong> Premium modular kitchen with materials — Kanbr sources boards, laminates and edgebands. Pick a build quality that fits.</p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                        {HOW_STANDARD.map((st, i) => (
                            <div key={i} className="border border-slate-700 bg-slate-800/50 p-4 relative">
                                <div className="text-[10px] font-display font-bold text-amber-500 absolute top-3 right-4 text-lg">0{i + 1}</div>
                                <div className="w-9 h-9 bg-amber-600 text-white flex items-center justify-center mb-2">{st.icon}</div>
                                <h3 className="font-display font-semibold text-white text-sm leading-snug">{st.title}</h3>
                            </div>
                        ))}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {STANDARD_TIERS.map((t) => (
                            <div key={t.key} className={`p-5 border ${t.highlight ? 'border-amber-500 ring-2 ring-amber-500 bg-slate-800' : 'border-slate-700 bg-slate-800/60'} text-white`}>
                                {t.highlight && <div className="text-[9px] uppercase tracking-widest text-amber-400 mb-1 font-semibold">Popular</div>}
                                <div className="eyebrow mb-1 text-slate-400">{t.label}</div>
                                <div className="font-display text-2xl font-bold">{inr(t.price)}<span className="text-xs font-medium text-slate-400"> /board</span></div>
                                <div className="text-[11px] mt-0.5 text-slate-400">+ GST + Transport</div>
                                <div className="mt-3 text-sm font-medium text-slate-200">{t.material}</div>
                                <p className="text-xs mt-1 leading-relaxed text-slate-400">{t.blurb}</p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-8 text-center">
                        <Link to="/standard-package" className="btn-accent rounded-none inline-flex" data-testid="explore-standard">
                            Explore Standard Package <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Section 3: Pre-Crafted Modules */}
            <section id="pre-crafted" className="bg-white">
                <div className="kanbr-container py-14">
                    <div className="text-center mb-8">
                        <div className="eyebrow text-slate-500 mb-2">Offering 03</div>
                        <h2 className="font-display text-2xl md:text-3xl font-bold text-amber-600 leading-tight tracking-tight">Pre-Crafted Modules</h2>
                        <p className="text-slate-700 mt-3 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">Prefabricated kitchen cabinet boxes, wardrobes and more — <strong>build your own modular wardrobe online</strong> and get it factory-finished at your doorstep.</p>
                    </div>

                    <div className="space-y-8">
                        {showcaseGroups.map((g) => (
                            <div key={g.title}>
                                <div className="flex items-baseline justify-between mb-3">
                                    <h3 className="font-display text-lg md:text-xl font-semibold text-slate-900">{g.title}</h3>
                                    <Link
                                        to={`/pre-crafted-modules?${g.link}`}
                                        className="text-xs text-blue-900 font-medium hover:underline"
                                        data-testid={`showcase-more-${g.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                                    >
                                        View more →
                                    </Link>
                                </div>
                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                    {g.items.map((p) => <ProductCard key={p.id} product={p} onOpen={setModalProduct} />)}
                                    {g.items.length === 0 && <div className="col-span-full text-sm text-slate-400 py-6">Coming soon.</div>}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-10 text-center">
                        <Link to="/pre-crafted-modules" className="btn-primary rounded-none inline-flex" data-testid="see-all-modules">
                            See all modules <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Section 4: Ideation & Designing */}
            <section id="ideation" className="bg-slate-900 text-white border-t border-slate-700">
                <div className="kanbr-container py-14 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                    <div>
                        <div className="eyebrow text-slate-400 mb-2">Offering 04</div>
                        <h2 className="font-display text-2xl md:text-3xl font-bold text-amber-500 leading-tight tracking-tight">Ideation &amp; Designing</h2>
                        <p className="text-slate-300 mt-3 text-sm md:text-base leading-relaxed max-w-lg">
                            Turnkey modular kitchen execution — we ideate, design, procure and manufacture. Perfect for homeowners who want a hands-off, custom modular wardrobe company experience.
                        </p>
                        <div className="mt-6">
                            <button onClick={() => setLeadOpen(true)} className="btn-accent rounded-none" data-testid="ideation-cta">
                                <Sparkles className="w-4 h-4" /> Start Ideation
                            </button>
                        </div>
                    </div>
                    <img src={imgUrl('/api/static/site/ideation.jpg')} alt="Bespoke wardrobe design" className="w-full aspect-[4/3] object-cover" data-testid="ideation-image" />
                </div>
            </section>

            {/* Testimonials */}
            <section className="kanbr-container py-12">
                <div className="max-w-2xl mb-6 text-center mx-auto">
                    <div className="eyebrow mb-2">Client &amp; partner voices</div>
                    <h2 className="font-display text-2xl md:text-3xl font-bold text-slate-900 leading-tight">Trusted by homeowners, designers &amp; architects.</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                        { name: 'Rakhi Malhotra', role: 'Homeowner · New Delhi', body: 'Zero mess at home. My wardrobe arrived pre-cut and drilled — finish is spotless.' },
                        { name: 'Ar. Yash Bhatia', role: 'Bhatia Studio · Gurgaon', body: 'We route most wardrobe carcass work to Kanbr. Turnaround is quick, tolerances tight.' },
                        { name: 'Sanjay Verma', role: 'Homeowner · Noida', body: 'Gold ply option is genuinely premium — soft-close everything, honest pricing.' },
                    ].map((t, i) => (
                        <blockquote key={i} className="border border-slate-200 bg-white p-5">
                            <Quote className="w-5 h-5 text-amber-600 mb-2" />
                            <p className="text-slate-700 leading-relaxed text-sm">{t.body}</p>
                            <div className="mt-4 flex items-center gap-1">
                                {[...Array(5)].map((_, k) => <Star key={k} className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />)}
                            </div>
                            <div className="mt-2">
                                <div className="font-display font-semibold text-slate-900 text-sm">{t.name}</div>
                                <div className="text-[11px] text-slate-500 mt-0.5">{t.role}</div>
                            </div>
                        </blockquote>
                    ))}
                </div>
            </section>

            <ProductDetailModal product={modalProduct} open={!!modalProduct} onOpenChange={(v) => !v && setModalProduct(null)} />
            <LeadCaptureModal open={leadOpen} onOpenChange={setLeadOpen} source="ideation" defaultMessage="Hi Kanbr, I want a turnkey modular kitchen/wardrobe — ideation, design, procurement & manufacturing." />
        </div>
    );
}
